<template>
      <div class="pagination-wrapper">
            <div v-if="allowFirstAndLast" class="item" @click="goToFirstPage">&laquo;&laquo;</div>
            <div v-show="batch > 0" class="item" @click="onBatchChange(-1)">&laquo;</div>
            <div
                  v-for="page in computedPages"
                  class="item"
                  :class="{ active: page === activePage }"
                  :key="page"
                  :ref="`page${page}`"
                  @click="onPageClick(page)"
            >{{ page }}</div>
            <div v-show="batch < numberOfBatches" class="item" @click="onBatchChange(+1)">&raquo;</div>
            <div v-if="allowFirstAndLast" class="item" @click="goToLastPage">&raquo;&raquo;</div>
      </div>
</template>

<script>
export default {
	name: "j-pagination",
	props: {
		amount: {
			type: Number,
			required: true
		},
		perPage: {
			type: Number,
			default: 0
		},
		activePage: {
			type: Number,
			default: 1
		},
		// How many pages to show in pagination
		maxItemsInBatch: {
			type: Number,
			default: 5,
			required: false
		},

		allowFirstAndLast: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			// Calcualte What batch of pagination to show
			// Batch is simply a page of pagination - user can change batch via those arrow buttons
			batch: this.getBatch()
		}
	},

	computed: {
		computedPages() {
			const allPages = Array(this.numberOfPages)
				.fill(0)
				.map((item, i) => i + 1)

			return allPages.slice(
				this.batch * this.maxItemsInBatch,
				this.batch * this.maxItemsInBatch + this.maxItemsInBatch
			)
		},

		numberOfPages() {
			return Math.ceil(this.amount / this.perPage)
		},

		numberOfBatches() {
			return (
				Math.ceil(this.numberOfPages / this.maxItemsInBatch) - 1
			)
		}
	},

	methods: {
		getBatch() {
			return Math.floor(
				(this.activePage - 1) / this.maxItemsInBatch
			) > this.numberOfBatches
				? this.numberOfBatches
				: Math.floor(
						(this.activePage - 1) / this.maxItemsInBatch
				  )
		},

		onBatchChange(step) {
			if (this.batch + step < 0) this.batch = 0
			else if (this.batch + step > this.numberOfBatches)
				this.batch = this.numberOfBatches
			else this.batch = this.batch + step

			// If going backwards, select last page, otherwise select first page of new batch
			step + 1 === 2
				? this.onPageClick(
						this.batch * this.maxItemsInBatch + 1
				  )
				: this.onPageClick(
						(this.batch + 1) * this.maxItemsInBatch
				  )
		},

		onPageClick(page) {
			this.$emit("click", page)
		},

		goToFirstPage() {
			this.onPageClick(1)
			this.batch = 0
		},

		goToLastPage() {
			this.onPageClick(this.numberOfPages)
			this.batch = this.numberOfBatches
		}
	},

	watch: {
		// When active page changes, reset batch
		activePage: {
			handler() {
				this.batch = this.getBatch()
			},
			immediate: false
		}
	}
}
</script>

<style scoped>
@import "../../assets/css/tokens.css";

.pagination-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	flex-wrap: wrap;
}

.pagination-wrapper > .item {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 40px;
	height: 40px;
	margin: 0 var(--spacing1) var(--spacing2) var(--spacing1);
	background-color: var(--color5);
	border: 1px solid var(--color7);
	border-radius: var(--borderRadius1);
	box-shadow: 0 2px 16px -1px var(--color6-alpha-10);
	font-size: var(--fontSize3);
	cursor: pointer;
	user-select: none;
}

.pagination-wrapper > .item:hover {
	background-color: var(--color7-light-5);
}

.pagination-wrapper > .item.active {
	background-color: var(--color1);
	border-color: var(--corlo2);
	color: var(--color5);
	cursor: auto;
	pointer-events: none;
}

@media (--md) {
	.pagination-wrapper > .item {
		width: 32px;
		height: 32px;
	}
}
</style>
